import moment from 'moment-timezone';
import Dinero from 'dinero.js';
import store from '@/bootstrap/vuex';
import router from '@/bootstrap/router';

// Tenant timezone is a hard invariant — tenants cannot sign up without one.
// A missing value is always a bug (in sign-up, the org-switch payload, or a
// caller that forgot to wait for the store to hydrate), so we fail loudly
// with an alert and an exception rather than silently guessing via
// moment.tz.guess() or falling through to the browser's tz.
//
// Tenant timezone resolution: read from the active organisation's settings
// slice of the Vuex store, keyed by the current route's `organisation` slug
// param. Callers can override via explicit `timeZone` argument for legitimate
// edge cases (e.g. rendering a UTC audit timestamp) — passing null / omitting
// the argument is the normal path and resolves via the store.
function tenantTimezone() {
    const orgId = router?.currentRoute?.value?.params?.organisation;
    const tz = store?.state?.organisation?.[orgId]?.settings?.timezone;
    if (!tz) {
        const message = 'Tenant timezone is missing from the active organisation — this should never happen.';
        if (typeof window !== 'undefined' && typeof window.flashMessage === 'function') {
            window.flashMessage({ type: 'error', message });
        }
        throw new Error(message);
    }

    return tz;
}

function requireTimezone(timeZone) {
    return timeZone || tenantTimezone();
}

// Extract calendar components (y/m/d/h/mi/s) from an input. Accepts:
//  - Date:  browser-local getters (components are read as-is)
//  - string: 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm:ss' or 'YYYY-MM-DD HH:mm:ss'
// Used only by the input helpers (tenant-tz wall-clock → UTC), where the
// caller's contract is that the components represent the user's picked
// wall-clock, not a UTC instant.
function extractCalendarComponents(input) {
    if (input instanceof Date) {
        return {
            y: input.getFullYear(),
            m: input.getMonth(),
            d: input.getDate(),
            h: input.getHours(),
            mi: input.getMinutes(),
            s: input.getSeconds(),
        };
    }

    if (typeof input === 'string') {
        const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(input);
        if (!match) {
            throw new Error(`Unparseable calendar date string: "${input}" — expected YYYY-MM-DD or YYYY-MM-DD HH:mm:ss`);
        }

        return {
            y: Number(match[1]),
            m: Number(match[2]) - 1,
            d: Number(match[3]),
            h: match[4] ? Number(match[4]) : 0,
            mi: match[5] ? Number(match[5]) : 0,
            s: match[6] ? Number(match[6]) : 0,
        };
    }

    throw new Error('Invalid calendar-date input: expected Date or "YYYY-MM-DD" string');
}

const helpers = {
    getAnchorCurrencyId() {
        // USD currency id is 151
        return 151;
    },

    centsToDollars(cents, options = { locale: 'en-US', currency: 'USD' }) {
        // if cents is undefined, do nothing.
        if (typeof cents == 'undefined') {
            return;
        }

        return Dinero({
            amount: +cents,
            currency: options.currency,
        })
            .setLocale(options.locale)
            .toFormat('0,0.00');
    },

    centsToDollarsForFinancialReport(cents, options = { locale: 'en-US', currency: 'USD' }) {
        let value = this.centsToDollars(cents, options);

        if (cents < 0) {
            value = `(${value.replace('-', '')})`;
        }

        return value;
    },

    centsToDollarsWithSymbol(cents, options = { locale: 'en-US', currency: 'USD' }) {
        return Dinero({
            amount: +cents,
            currency: options.currency,
        })
            .setLocale(options.locale)
            .toFormat('$0,0.00');
    },

    toDecimal(value, places) {
        return parseFloat(value).toFixed(places);
    },

    // Display helpers — UTC instant → tenant-tz representation.

    /**
     * Returns a Date whose LOCAL components (getFullYear, getMonth, getDate,
     * getHours, getMinutes, getSeconds) read as the tenant-tz wall-clock.
     *
     * DISPLAY-ONLY. The underlying UTC timestamp is synthetic — it does NOT
     * represent the real UTC instant. Therefore:
     *
     *   ✅ Pass to $d(x, 'short' | 'long') — Intl reads local components
     *   ✅ Read components: x.getFullYear(), x.getMonth(), x.getDate()
     *   ❌ x.toISOString() — returns a wrong UTC string
     *   ❌ x.getTime() — returns a wrong epoch ms, not the input's epoch
     *   ❌ JSON.stringify(x) / sending in a request body
     *   ❌ new Date(x) round-trip, Date-vs-Date arithmetic
     *
     * For UTC ↔ wall-clock conversion, use toUTC / startOfDayUTC / endOfDayUTC.
     */
    toTenantWallClockDate(date, timeZone = null) {
        const tz = requireTimezone(timeZone);
        const m = moment.utc(date).tz(tz);

        // Return a Date whose local components match the tenant-tz components,
        // so $d() (which reads components via Intl in the browser's tz) renders
        // the tenant's calendar day regardless of where the browser is running.
        return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second());
    },

    /**
     * UTC instant → formatted "MMM DD, YYYY" string in the tenant's timezone.
     * Returns a plain string, safe to render anywhere. Throws + flashes when
     * the tenant timezone is missing from the store (see tenantTimezone()).
     */
    toShortDate(date, timeZone = null) {
        const tz = requireTimezone(timeZone);
        return moment.utc(date).tz(tz).format('MMM DD, YYYY');
    },

    /**
     * UTC instant → moment-formatted string in the tenant's timezone, for
     * arbitrary formats (query params, filenames, non-$d contexts). Returns a
     * plain string. Throws + flashes when the tenant timezone is missing.
     */
    toDateFormat(date, format = 'YYYY-MM-DD', timeZone = null) {
        const tz = requireTimezone(timeZone);
        return moment.utc(date).tz(tz).format(format);
    },

    /**
     * UTC instant → relative phrase ("3 days ago", "in 2 hours") anchored in
     * the tenant's timezone, not the browser's. Ensures two users in different
     * browser tzs see the same relative-time description for the same instant.
     * Throws + flashes when the tenant timezone is missing.
     */
    fromNow(date, timeZone = null) {
        const tz = requireTimezone(timeZone);
        return moment.utc(date).tz(tz).fromNow();
    },

    // Input helpers — tenant-tz wall-clock → UTC ISO (for POSTs).

    /**
     * Tenant-tz wall-clock → UTC ISO string, for request bodies.
     *
     * Input contract: a `Date` whose local components represent the user's
     * picked wall-clock (components read verbatim, no tz shift), or a naive
     * string `'YYYY-MM-DD'` / `'YYYY-MM-DD HH:mm:ss'`. The components are
     * treated as tenant-tz (sign-up guarantees one). Throws + flashes when
     * the tenant timezone is missing.
     */
    toUTC(date, timeZone = null) {
        const tz = requireTimezone(timeZone);
        const { y, m, d, h, mi, s } = extractCalendarComponents(date);
        return moment.tz([y, m, d, h, mi, s], tz).utc().format();
    },

    /**
     * Tenant-tz "today at midnight" → UTC ISO string. Use for tenant-anchored
     * "as of today" query filters. Throws + flashes when the tenant timezone
     * is missing.
     */
    todayUTC(timeZone = null) {
        const tz = requireTimezone(timeZone);
        return moment.tz(tz).startOf('day').utc().format();
    },

    /**
     * Tenant-tz calendar-day midnight (00:00:00.000) → UTC ISO string. Use
     * for the lower bound of day-scoped query filters. Throws + flashes when
     * the tenant timezone is missing.
     */
    startOfDayUTC(date, timeZone = null) {
        const tz = requireTimezone(timeZone);
        const { y, m, d } = extractCalendarComponents(date);
        return moment.tz([y, m, d, 0, 0, 0, 0], tz).utc().format();
    },

    /**
     * Tenant-tz calendar-day end-of-day (23:59:59.999) → UTC ISO string. Use
     * for the upper bound of day-scoped query filters. Throws + flashes when
     * the tenant timezone is missing.
     */
    endOfDayUTC(date, timeZone = null) {
        const tz = requireTimezone(timeZone);
        const { y, m, d } = extractCalendarComponents(date);
        return moment.tz([y, m, d, 23, 59, 59, 999], tz).utc().format();
    },

    /**
     * Tenant-tz first-of-the-month at midnight → UTC ISO string. Use for the
     * lower bound of monthly report filters. Throws + flashes when the tenant
     * timezone is missing.
     */
    startOfMonthUTC(date, timeZone = null) {
        const tz = requireTimezone(timeZone);
        const { y, m } = extractCalendarComponents(date);
        return moment.tz([y, m, 1, 0, 0, 0, 0], tz).utc().format();
    },

    /**
     * Tenant-tz last-moment-of-the-month (last day, 23:59:59.999) → UTC ISO
     * string. Use for the upper bound of monthly report filters. Throws +
     * flashes when the tenant timezone is missing.
     */
    endOfMonthUTC(date, timeZone = null) {
        const tz = requireTimezone(timeZone);
        const { y, m } = extractCalendarComponents(date);
        return moment.tz([y, m, 1, 0, 0, 0, 0], tz).endOf('month').utc().format();
    },

    kebabCase(str) {
        return str
            .replace(/\s+/g, '-') // Replace whitespace with hyphens
            .replace(/([a-z])([A-Z])/g, '$1-$2') // Convert camelCase to kebab-case
            .toLowerCase(); // Convert to lowercase
    },

    roundToInteger(value, options = { locale: 'en-US', currency: 'JMD' }) {
        return Math.round(value);
    },

    roundCents(value, options /* = { locale: 'en-US', currency: 'JMD' } */) {
        return Math.round(value);
    },

    toCents(formattedNumber, options = { locale: 'en-US', currency: 'JMD' }) {
        // Create a formatter with the given locale and currency.
        const formatter = new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: options.currency,
        });

        // Use the formatter to determine the decimal and grouping separators.
        const formattedParts = formatter.formatToParts(12345.6789); // Magic number

        let decimalSeparator = '.';
        let groupSeparator = ',';
        let currencySymbol = '';

        for (const part of formattedParts) {
            if (part.type === 'decimal') {
                decimalSeparator = part.value;
            } else if (part.type === 'group') {
                groupSeparator = part.value;
            } else if (part.type === 'currency') {
                currencySymbol = part.value;
            }
        }

        // Use resolvedOptions method to get the fraction digits for the current currency
        const fractionDigits = formatter.resolvedOptions().maximumFractionDigits;

        // Remove the currency symbol from the value
        const numberWithoutCurrencySymbol = formattedNumber.replace(new RegExp(`\\${currencySymbol}`, 'g'), '');

        // Remove the grouping separator, replace the decimal separator with a period, parse as a float.
        const numberWithoutGroupSeparator = numberWithoutCurrencySymbol.replace(new RegExp(`\\${groupSeparator}`, 'g'), '');
        const numberWithStandardDecimalSeparator = numberWithoutGroupSeparator.replace(decimalSeparator, '.');
        const numberAsFloat = parseFloat(numberWithStandardDecimalSeparator);

        // Custom rounding based on the locale and currency
        let numberInSmallestUnit;
        if (options.locale === 'fr-CH' && options.currency === 'CHF') {
            // TODO write script to parse this currency
            throw new Error('This currency is not supported. Please contact administrator');

            // Round to the nearest 5 cents
            numberInSmallestUnit = Math.round((numberAsFloat * Math.pow(10, fractionDigits)) / 5) * 5;
        } else {
            // General rounding
            numberInSmallestUnit = Math.round(numberAsFloat * Math.pow(10, fractionDigits));
        }

        return numberInSmallestUnit;
    },

    formatDateForFilename(date, timeZone = null) {
        const tz = requireTimezone(timeZone);
        return moment.utc(date).tz(tz).format('MMM-DD-YYYY');
    },

    /* Method generates a route for a given item resource
     *
     * @param {*} organisation
     * @param {*} item
     * @returns {} vue route
     */
    getResourceRoute(organisation, item) {
        let route = {};

        switch (item.resource) {
            case 'invoices':
                route = {
                    name: 'accounts-receivable.invoices.view-invoice',
                    params: {
                        organisation,
                        id: item.resource_id,
                    },
                };
                break;

            case 'sales':
                route = {
                    name: 'accounts-receivable.sales.view-sale',
                    params: {
                        organisation,
                        id: item.resource_id,
                    },
                };
                break;

            case 'expenses':
                route = {
                    name: 'accounts-payable.expenses.view-expense',
                    params: {
                        organisation,
                        id: item.resource_id,
                    },
                };
                break;

            case 'purchase_orders':
                route = {
                    name: 'accounts-payable.purchase-orders.view-purchase-order',
                    params: {
                        organisation,
                        id: item.resource_id,
                    },
                };
                break;

            case 'bank_transactions':
                let account = JSON.parse(item.resource_id);
                route = { name: 'banking.bank-transactions.view-bank-transaction', params: { organisation, id: account.bank_account_id, transactionId: account.transaction_id } };
                break;

            case 'team_member_deductions':
                route = { name: 'human-resource.team-member-deductions.view-team-member-deduction', params: { organisation, id: item.resource_id } };
                break;

            case 'tax_bills':
                route = { name: 'tax-compliance.tax-bills.view-tax-bill', params: { organisation, id: item.resource_id } };
                break;

            case 'payroll_batches':
                let payroll = JSON.parse(item.resource_id);
                route = { name: 'human-resource.payrolls.view-payroll-batch', params: { organisation, id: payroll.payroll_id, batchId: payroll.payroll_batch_id } };
                break;

            case 'issued_loans':
                route = {
                    name: 'loans.issued-loan.view-issued-loan',
                    params: {
                        organisation,
                        id: item.resource_id,
                    },
                };
                break;

            case 'received_loans':
                route = {
                    name: 'loans.received-loan.view-received-loan',
                    params: {
                        organisation,
                        id: item.resource_id,
                    },
                };
                break;

            case 'investments':
                route = {
                    name: 'investment.investments.view-investment',
                    params: {
                        organisation,
                        id: item.resource_id,
                    },
                };
                break;

            default:
                route = { name: 'errors.not-found', params: { organisation } };
                break;
        }

        return route;
    },

    getForegroundColour($data) {
        let foreground = null;
        switch ($data.length) {
            // Red
            case 1:
            case 2:
            case 3:
            case 4:
                foreground = '#EF4444';
                break;

            // Orange
            case 5:
            case 6:
            case 7:
            case 8:
                foreground = '#F97316';
                break;

            // Light Blue
            case 9:
            case 10:
                foreground = '#0EA5E9';
                break;

            // Emerald
            case 11:
            case 12:
                foreground = '#10B981';
                break;

            // Fuchsia
            case 13:
            case 14:
                foreground = '#D946EF';
                break;

            // Cyan
            case 15:
            case 16:
                foreground = '#06B6D4';
                break;

            // Indigo
            case 17:
            case 18:
                foreground = '#6366F1';
                break;

            // Blue Gray
            default:
                foreground = '#64748B';
        }

        return foreground;
    },

    getBackgroundColour($data) {
        let background = null;

        switch ($data.length) {
            // Red
            case 1:
            case 2:
            case 3:
            case 4:
                background = '#FEE2E2';
                break;

            // Orange
            case 5:
            case 6:
            case 7:
            case 8:
                background = '#FFEDD5';
                break;

            // Light Blue
            case 9:
            case 10:
                background = '#E0F2FE';

                break;

            // Emerald
            case 11:
            case 12:
                background = '#D1FAE5';

                break;

            // Fuchsia
            case 13:
            case 14:
                background = '#FAE8FF';

                break;

            // Cyan
            case 15:
            case 16:
                background = '#CFFAFE';

                break;

            // Indigo
            case 17:
            case 18:
                background = '#E0E7FF';

                break;

            // Blue Gray
            default:
                background = '#F1F5F9';
        }

        return background;
    },
};

export default helpers;
